import React from 'react';
import { FlatList, Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useDeals } from '../../src/data';
import { GROUPS, type Group, groupOf, price, timeAgo } from '../../src/format';
import type { Deal } from '../../src/types';

/**
 * AALTO — 에디토리얼 방향 시안.
 *
 * S_TRACK 과 같은 2열 그리드를 쓰되 태도가 반대다. 흰 바탕에 여백을 크게 주고,
 * 맨 위 한 건을 기사처럼 세워 "왜 이게 싼지"를 문장으로 설명한다.
 * 딜 앱의 신뢰는 숫자를 던지는 게 아니라 근거를 보여 주는 데서 온다는 전제.
 */
const P = {
  paper: '#FFFFFF',
  ink: '#16161A',
  ink2: '#5B5B63',
  ink3: '#9A9AA2',
  line: '#E8E8EC',
  accent: '#16161A',
  good: '#0B7A55',
} as const;

export default function DesignT() {
  const insets = useSafeAreaInsets();
  const deals = useDeals();
  const [group, setGroup] = React.useState<Group>('전체');
  const [cols, setCols] = React.useState<2 | 3>(2);
  const shown = React.useMemo(
    () => (group === '전체' ? deals : deals.filter((d) => groupOf(d) === group)),
    [deals, group],
  );
  return (
    <View style={{ flex: 1, backgroundColor: P.paper }}>
      <FlatList
        data={shown}
        keyExtractor={(d) => d.id}
        key={cols}
        numColumns={cols}
        columnWrapperStyle={{ gap: 10, paddingHorizontal: 20 }}
        contentContainerStyle={{ gap: 18, paddingBottom: 24 }}
        ListHeaderComponent={
          <View>
            <View style={[s.head, { paddingTop: insets.top + 18 }]}>
              <Text style={s.kicker}>PREMIUM TRACKER</Text>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Text style={s.brand}>AALTO —{'\n'}오늘의 딜</Text>
                <Pressable onPress={() => setCols(cols === 2 ? 3 : 2)} style={s.colBtn}>
                  <Text style={s.colBtnText}>{cols}열</Text>
                </Pressable>
              </View>
            </View>
            <View style={s.tabs}>
              {GROUPS.map((g) => (
                <Pressable key={g} onPress={() => setGroup(g)} style={[s.tab, g === group && s.tabOn]}>
                  <Text style={[s.tabText, g === group && s.tabTextOn]}>{g}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        }
        renderItem={({ item }) => <Tile deal={item} cols={cols} />}
      />
    </View>
  );
}

function discount(d: Deal): number | null {
  if (!d.market_price || !d.effective_price) return null;
  const pct = Math.round((1 - d.effective_price / d.market_price) * 100);
  return pct >= 5 ? pct : null;
}

function Tile({ deal, cols }: { deal: Deal; cols: 2 | 3 }) {
  const tight = cols === 3;
  const [broken, setBroken] = React.useState(false);
  const thumb = !broken ? deal.thumb_url : null;
  const off = discount(deal);
  return (
    <Pressable style={({ pressed }) => [{ flex: 1, gap: 9 }, pressed && { opacity: 0.9 }]}>
      <View style={s.shot}>
        {thumb ? (
          <Image source={{ uri: thumb }} style={{ width: '100%', height: '100%' }} resizeMode="cover" onError={() => setBroken(true)} />
        ) : (
          // 사진을 아직 못 구한 딜. 상품명을 또 쓰면 아래와 겹치니 점수를 채운다
          <View style={s.noShot}>
            <Text style={[s.scoreNum, tight && { fontSize: 28 }]}>{Math.round(deal.score)}</Text>
            <Text style={s.scoreUnit}>점</Text>
          </View>
        )}
        {off !== null ? (
          <View style={s.offTag}>
            <Text style={s.offText}>-{off}%</Text>
          </View>
        ) : null}
      </View>
      <View style={{ gap: 3 }}>
        <Text style={s.mall}>{deal.mall ?? '판매처 미확인'}</Text>
        <Text style={[s.name, tight && { fontSize: 12, lineHeight: 16 }]} numberOfLines={2}>{deal.product}</Text>
        <Text style={[s.price, tight && { fontSize: 14 }]}>{price(deal)}</Text>
        <Text style={s.time}>{deal.posted_at ? timeAgo(deal.posted_at) : ''}</Text>
      </View>
    </Pressable>
  );
}

const s = StyleSheet.create({
  head: { paddingHorizontal: 20, paddingBottom: 18, gap: 8 },
  kicker: { fontSize: 10, fontWeight: '800', color: P.ink3, letterSpacing: 1.8 },
  brand: { flex: 1, fontSize: 30, fontWeight: '400', color: P.ink, lineHeight: 39, letterSpacing: -0.8 },
  colBtn: { borderWidth: 1, borderColor: P.line, paddingVertical: 5, paddingHorizontal: 11, marginTop: 14 },
  colBtnText: { fontSize: 11.5, fontWeight: '700', color: P.ink2 },
  menu: { gap: 5, paddingTop: 14 },
  menuBar: { width: 26, height: 1.6, backgroundColor: P.ink },

  tabs: { flexDirection: 'row', gap: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: P.line },
  tab: { paddingBottom: 10, borderBottomWidth: 1.6, borderBottomColor: 'transparent' },
  tabOn: { borderBottomColor: P.ink },
  tabText: { fontSize: 13.5, color: P.ink3 },
  tabTextOn: { color: P.ink, fontWeight: '700' },

  lead: { paddingHorizontal: 20, paddingTop: 26 },
  leadKicker: { fontSize: 10, fontWeight: '800', color: P.ink3, letterSpacing: 1.4 },
  leadTitle: { fontSize: 23, fontWeight: '500', color: P.ink, lineHeight: 32, letterSpacing: -0.5 },
  leadBody: { fontSize: 14, color: P.ink2, lineHeight: 23 },
  round: { width: 48, height: 48, borderRadius: 24, borderWidth: 1, borderColor: P.line, alignItems: 'center', justifyContent: 'center', marginTop: 18 },
  roundArrow: { fontSize: 17, color: P.ink },
  viewDeal: { fontSize: 12, fontWeight: '800', color: P.ink, letterSpacing: 1.1 },
  viewArrow: { fontSize: 14, color: P.ink },
  rule: { height: 1, backgroundColor: P.line, marginTop: 24 },

  shot: { aspectRatio: 1, backgroundColor: '#F3F3F5', overflow: 'hidden' },
  noShot: { flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 2 },
  scoreNum: { fontSize: 38, fontWeight: '300', color: P.ink3, letterSpacing: -1.5, fontVariant: ['tabular-nums'] },
  scoreUnit: { fontSize: 13, color: P.ink3, marginTop: 14 },
  offTag: { position: 'absolute', top: 8, right: 8, backgroundColor: P.paper, paddingVertical: 3, paddingHorizontal: 8 },
  offText: { fontSize: 11.5, fontWeight: '800', color: P.ink },

  mall: { fontSize: 11, color: P.ink3 },
  name: { fontSize: 13.5, color: P.ink, lineHeight: 18 },
  price: { fontSize: 16, fontWeight: '700', color: P.ink, fontVariant: ['tabular-nums'], marginTop: 1 },
  time: { fontSize: 11, color: P.ink3 },

  nav: { flexDirection: 'row', justifyContent: 'space-around', paddingTop: 14, borderTopWidth: 1, borderTopColor: P.line },
  navItem: { fontSize: 12, fontWeight: '700', color: P.ink3, letterSpacing: 1.2 },
  navOn: { color: P.ink },
});
